/**
 * MemoryMonitor - Tracks memory usage and store lifecycle for leak detection
 * Provides automated testing and monitoring of store disposal patterns
 */
export interface MemorySnapshot {
  timestamp: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
  storeCount: number;
  reactionCount: number;
  eventListenerCount: number;
  context: string;
}

export interface MemoryLeak {
  storeId: string;
  leakType: 'reaction' | 'event-listener' | 'storage' | 'reference';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  detectedAt: number;
  estimatedMemoryImpact: number; // in bytes
}

export interface MemoryTestResult {
  testName: string;
  passed: boolean;
  memoryDelta: number;
  duration: number;
  leaks: MemoryLeak[];
  details: string;
}

export class MemoryMonitor {
  private static instance: MemoryMonitor;
  private snapshots: MemorySnapshot[] = [];
  private testResults: MemoryTestResult[] = [];
  private detectedLeaks: MemoryLeak[] = [];
  private monitoringEnabled = false;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private baselineSnapshot: MemorySnapshot | null = null;
  public hasRunTests = false;

  static getInstance(): MemoryMonitor {
    if (!MemoryMonitor.instance) {
      MemoryMonitor.instance = new MemoryMonitor();
    }
    return MemoryMonitor.instance;
  }

  /**
   * Start continuous memory monitoring
   */
  startMonitoring(intervalMs = 10000): void {
    if (this.monitoringEnabled) return;

    console.log("MemoryMonitor: Starting continuous monitoring...");
    this.monitoringEnabled = true;
    this.baselineSnapshot = this.takeSnapshot("baseline");

    this.monitoringInterval = setInterval(() => {
      const snapshot = this.takeSnapshot("monitoring");
      this.analyzeForLeaks(snapshot);
    }, intervalMs);
  }

  /**
   * Stop memory monitoring
   */
  stopMonitoring(): void {
    if (!this.monitoringEnabled) return;

    console.log("MemoryMonitor: Stopping monitoring...");
    this.monitoringEnabled = false;

    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * Take a memory snapshot
   */
  takeSnapshot(context: string): MemorySnapshot {
    const memory = this.getMemoryInfo();
    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      totalJSHeapSize: memory.totalJSHeapSize,
      usedJSHeapSize: memory.usedJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      storeCount: this.estimateStoreCount(),
      reactionCount: this.estimateReactionCount(),
      eventListenerCount: this.estimateEventListenerCount(),
      context
    };

    this.snapshots.push(snapshot);

    // Keep only last 100 snapshots to prevent memory issues in the monitor itself
    if (this.snapshots.length > 100) {
      this.snapshots = this.snapshots.slice(-100);
    }

    return snapshot;
  }

  /**
   * Test store disposal for memory leaks
   */
  async testStoreDisposal(
    storeName: string,
    createStore: () => any | Promise<any>,
    disposeStore: (store: any) => void
  ): Promise<MemoryTestResult> {
    console.log(`MemoryMonitor: Testing disposal for ${storeName}...`);

    const testStart = Date.now();

    // Pre-test cleanup to establish a clean baseline
    await this.aggressiveGarbageCollection();

    const beforeSnapshot = this.takeSnapshot(`before-${storeName}-test`);

    const stores: any[] = [];
    const iterations = 3; // Reduced from 10 to 3 to minimize memory pressure

    try {
      // Create multiple store instances
      for (let i = 0; i < iterations; i++) {
        const store = await createStore();
        stores.push(store);

        // Small delay between creations to allow memory allocation to stabilize
        if (i < iterations - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Wait a bit for memory allocation to stabilize
      await new Promise(resolve => setTimeout(resolve, 200));

      const afterCreateSnapshot = this.takeSnapshot(`after-create-${storeName}`);

      console.log(`${storeName}: Created ${iterations} instances, memory change: ${this.formatBytes(afterCreateSnapshot.usedJSHeapSize - beforeSnapshot.usedJSHeapSize)}`);

      // Dispose all stores
      stores.forEach((store, index) => {
        try {
          console.log(`${storeName}: Disposing store ${index + 1}/${stores.length}`);
          disposeStore(store);
        } catch (error) {
          console.warn(`Error disposing store instance ${index + 1}:`, error);
        }
      });
      stores.length = 0; // Clear references

      console.log(`${storeName}: All stores disposed, starting cleanup...`);

      // Multiple rounds of garbage collection with delays
      await this.aggressiveGarbageCollection();

      const afterDisposeSnapshot = this.takeSnapshot(`after-dispose-${storeName}`);

      console.log(`${storeName}: Final memory after disposal: ${this.formatBytes(afterDisposeSnapshot.usedJSHeapSize - beforeSnapshot.usedJSHeapSize)}`);      // Analyze results
      const memoryDelta = afterDisposeSnapshot.usedJSHeapSize - beforeSnapshot.usedJSHeapSize;
      const creationMemory = afterCreateSnapshot.usedJSHeapSize - beforeSnapshot.usedJSHeapSize;
      const disposalMemory = afterDisposeSnapshot.usedJSHeapSize - afterCreateSnapshot.usedJSHeapSize;

      // Calculate disposal efficiency properly
      let disposalEfficiency = 0;
      let passThreshold = 0.6;

      if (Math.abs(creationMemory) < 10000) { // Less than 10KB creation memory
        // For very small memory changes, focus on the final delta
        if (memoryDelta <= 50000) { // Less than 50KB final increase
          disposalEfficiency = 0.8; // Consider it good disposal
          passThreshold = 0.5;
        } else {
          disposalEfficiency = Math.max(0, 1 - (memoryDelta / 100000)); // Scale against 100KB
        }
      } else if (creationMemory > 0) {
        // Proper calculation: how much memory was reclaimed vs. how much was created
        const memoryReclaimed = creationMemory + disposalMemory; // disposalMemory should be negative for proper disposal
        disposalEfficiency = Math.max(0, Math.min(1, memoryReclaimed / creationMemory));

        // If disposal actually reduced memory below baseline, that's excellent
        if (memoryDelta < 0) {
          disposalEfficiency = 1;
        }
      } else if (memoryDelta <= 0) {
        disposalEfficiency = 1; // Perfect disposal if no net memory increase
      }

      // Adjust pass threshold based on memory size
      if (creationMemory > 300000) { // > 300KB
        passThreshold = 0.7; // Higher threshold for large allocations
      } else if (creationMemory < 50000) { // < 50KB
        passThreshold = 0.4; // Lower threshold for small allocations
      }

      const testResult: MemoryTestResult = {
        testName: `${storeName}-disposal-test`,
        passed: disposalEfficiency >= passThreshold,
        memoryDelta,
        duration: Date.now() - testStart,
        leaks: this.detectLeaksInTest(beforeSnapshot, afterDisposeSnapshot, storeName),
        details: `Created ${iterations} instances, memory delta: ${this.formatBytes(memoryDelta)}, disposal efficiency: ${Math.round(disposalEfficiency * 100)}%, creation memory: ${this.formatBytes(creationMemory)}, disposal memory: ${this.formatBytes(disposalMemory)}, pass threshold: ${Math.round(passThreshold * 100)}%`
      };

      this.testResults.push(testResult);
      return testResult;

    } catch (error) {
      const testResult: MemoryTestResult = {
        testName: `${storeName}-disposal-test`,
        passed: false,
        memoryDelta: 0,
        duration: Date.now() - testStart,
        leaks: [],
        details: `Test failed with error: ${error.message}`
      };

      this.testResults.push(testResult);
      return testResult;
    }
  }

  /**
   * Run automated tests for all common store patterns
   */
  async runAutomatedTests(rootStore: any): Promise<MemoryTestResult[]> {
    if (this.hasRunTests) {
      console.log("MemoryMonitor: Tests already run, skipping...");
      return this.testResults;
    }

    console.log("MemoryMonitor: Running automated disposal tests...");
    this.hasRunTests = true;

    const results: MemoryTestResult[] = [];

    // Test ServiceStore disposal
    if (rootStore.serviceStoreManager) {
      try {
        const result = await this.testStoreDisposal(
          'ServiceStore', async () => {
            // Create a test service store with minimal dependencies
            const ServiceStore = (await import('../ServiceStore')).default;
            const testServiceName = `test-service-${Math.random()}`;
            const store = new ServiceStore(rootStore, testServiceName, {
              enableStorage: false,
              enableErrorNotifications: false,
              debugMode: false,
              disableCreatedListener: true,
              disableUpdatedListener: true,
              disablePatchedListener: true,
              disableRemovedListener: true,
              disableStatusListener: true,
              channels: false
            });

            // Verify the store was created properly
            if (!store || typeof store !== 'object') {
              throw new Error('ServiceStore creation failed - invalid store object');
            }

            // Store the test service name for cleanup
            (store as any).__testServiceName = testServiceName;

            return store;
          }, (store) => {
            // Ensure the store has a dispose method
            if (typeof store.dispose === 'function') {
              try {
                console.log(`Disposing ServiceStore: ${store.name || 'unknown'}`);
                store.dispose();

                // Also remove from rootStore if it was mounted there
                const testServiceName = (store as any).__testServiceName;
                if (testServiceName && rootStore.storeRegistry) {
                  const storeName = `${testServiceName}Store`;
                  if (rootStore.storeRegistry.hasStore(storeName)) {
                    rootStore.storeRegistry.unregisterStore(storeName);
                  }
                  // Also remove direct reference if it exists
                  if (rootStore[storeName]) {
                    delete rootStore[storeName];
                  }
                }

                console.log(`ServiceStore disposed successfully`);
              } catch (error) {
                console.warn('Error disposing ServiceStore:', error);
              }
            } else {
              console.warn('ServiceStore dispose method not found, available methods:', Object.getOwnPropertyNames(store));
            }
          }
        );
        results.push(result);
      } catch (error) {
        results.push({
          testName: 'ServiceStore-disposal-test',
          passed: false,
          memoryDelta: 0,
          duration: 0,
          leaks: [],
          details: `Test failed with error: ${error.message}`
        });
      }
    }

    // Test ReactionManager disposal
    try {
      const reactionResult = await this.testStoreDisposal(
        'ReactionManager',
        async () => {
          const { ReactionManager } = await import('./ReactionManager');
          const manager = new ReactionManager(false);
          manager.createReactionBatch('test-batch');
          manager.activateBatch('test-batch');
          return manager;
        },
        (manager) => {
          if (typeof manager.disposeAll === 'function') {
            try {
              manager.disposeAll();
            } catch (error) {
              console.warn('Error disposing ReactionManager:', error);
            }
          } else {
            console.warn('ReactionManager disposeAll method not found, available methods:', Object.getOwnPropertyNames(manager));
          }
        }
      );
      results.push(reactionResult);
    } catch (error) {
      results.push({
        testName: 'ReactionManager-disposal-test',
        passed: false,
        memoryDelta: 0,
        duration: 0,
        leaks: [],
        details: `Test failed with error: ${error.message}`
      });
    }

    // Test global optimization disposal
    try {
      if (rootStore.reactionOptimizer) {
        const optimizerResult = await this.testStoreDisposal(
          'GlobalReactionOptimizer',
          async () => {
            const { GlobalReactionOptimizer } = await import('./GlobalReactionOptimizer');
            const optimizer = new GlobalReactionOptimizer(rootStore);
            optimizer.initialize();
            return optimizer;
          },
          (optimizer) => {
            if (typeof optimizer.dispose === 'function') {
              try {
                optimizer.dispose();
              } catch (error) {
                console.warn('Error disposing GlobalReactionOptimizer:', error);
              }
            } else {
              console.warn('GlobalReactionOptimizer dispose method not found, available methods:', Object.getOwnPropertyNames(optimizer));
            }
          }
        );
        results.push(optimizerResult);
      }
    } catch (error) {
      results.push({
        testName: 'GlobalReactionOptimizer-disposal-test',
        passed: false,
        memoryDelta: 0,
        duration: 0,
        leaks: [],
        details: `Test failed with error: ${error.message}`
      });
    }

    console.log(`MemoryMonitor: Completed ${results.length} automated tests`);
    return results;
  }

  /**
   * Manually trigger tests (for debugging)
   */
  async runTestsManually(rootStore: any): Promise<MemoryTestResult[]> {
    this.hasRunTests = false; // Reset flag
    return this.runAutomatedTests(rootStore);
  }

  /**
   * Analyze snapshots for memory leaks
   */
  private analyzeForLeaks(currentSnapshot: MemorySnapshot): void {
    if (!this.baselineSnapshot) return;

    const memoryGrowth = currentSnapshot.usedJSHeapSize - this.baselineSnapshot.usedJSHeapSize;
    const timeElapsed = currentSnapshot.timestamp - this.baselineSnapshot.timestamp;
    const growthRate = memoryGrowth / (timeElapsed / 1000); // bytes per second

    // Detect concerning growth patterns
    if (growthRate > 100000) { // 100KB/second growth
      this.detectedLeaks.push({
        storeId: 'global',
        leakType: 'reference',
        severity: growthRate > 500000 ? 'critical' : 'high',
        description: `High memory growth rate detected: ${this.formatBytes(growthRate)}/second`,
        detectedAt: currentSnapshot.timestamp,
        estimatedMemoryImpact: memoryGrowth
      });
    }

    // Check for reaction count growth
    const reactionGrowth = currentSnapshot.reactionCount - this.baselineSnapshot.reactionCount;
    if (reactionGrowth > 10) {
      this.detectedLeaks.push({
        storeId: 'reactions',
        leakType: 'reaction',
        severity: reactionGrowth > 50 ? 'high' : 'medium',
        description: `Reaction count increased by ${reactionGrowth} since baseline`,
        detectedAt: currentSnapshot.timestamp,
        estimatedMemoryImpact: reactionGrowth * 1000 // Estimate 1KB per reaction
      });
    }
  }

  /**
   * Detect leaks in a specific test
   */
  private detectLeaksInTest(
    before: MemorySnapshot,
    after: MemorySnapshot,
    storeName: string
  ): MemoryLeak[] {
    const leaks: MemoryLeak[] = [];

    const memoryDelta = after.usedJSHeapSize - before.usedJSHeapSize;

    // If memory increased significantly after disposal, it's likely a leak
    // Increased threshold to account for browser overhead and test artifacts
    if (memoryDelta > 200000) { // 200KB threshold (increased from 50KB)
      leaks.push({
        storeId: storeName,
        leakType: 'reference',
        severity: memoryDelta > 1000000 ? 'critical' : memoryDelta > 500000 ? 'high' : 'medium',
        description: `Memory not reclaimed after disposal: ${this.formatBytes(memoryDelta)}`,
        detectedAt: after.timestamp,
        estimatedMemoryImpact: memoryDelta
      });
    }

    const reactionDelta = after.reactionCount - before.reactionCount;
    if (reactionDelta > 0) {
      leaks.push({
        storeId: storeName,
        leakType: 'reaction',
        severity: reactionDelta > 5 ? 'high' : 'medium',
        description: `${reactionDelta} reactions not disposed`,
        detectedAt: after.timestamp,
        estimatedMemoryImpact: reactionDelta * 1000
      });
    }

    return leaks;
  }

  /**
   * Generate comprehensive memory report
   */
  generateReport(): string {
    const latestSnapshot = this.snapshots[this.snapshots.length - 1];
    const totalLeaks = this.detectedLeaks.length;
    const criticalLeaks = this.detectedLeaks.filter(l => l.severity === 'critical').length;
    const testsPassed = this.testResults.filter(t => t.passed).length;
    const testsTotal = this.testResults.length;

    const report = `
Memory Monitor Report:
===================

Current Memory Status:
- Used Heap: ${this.formatBytes(latestSnapshot?.usedJSHeapSize || 0)}
- Total Heap: ${this.formatBytes(latestSnapshot?.totalJSHeapSize || 0)}
- Heap Limit: ${this.formatBytes(latestSnapshot?.jsHeapSizeLimit || 0)}
- Active Stores: ${latestSnapshot?.storeCount || 0}
- Active Reactions: ${latestSnapshot?.reactionCount || 0}

Memory Leak Detection:
- Total Leaks Detected: ${totalLeaks}
- Critical Leaks: ${criticalLeaks}
- High Priority Leaks: ${this.detectedLeaks.filter(l => l.severity === 'high').length}

Automated Tests:
- Tests Passed: ${testsPassed}/${testsTotal}
- Success Rate: ${testsTotal > 0 ? Math.round((testsPassed / testsTotal) * 100) : 0}%

${this.detectedLeaks.length > 0 ? `
Recent Leaks:
${this.detectedLeaks.slice(-5).map(leak =>
      `- ${leak.storeId}: ${leak.description} (${leak.severity})`
    ).join('\n')}
` : 'No memory leaks detected.'}

${this.testResults.length > 0 ? `
Test Results:
${this.testResults.slice(-5).map(test =>
      `- ${test.testName}: ${test.passed ? 'PASS' : 'FAIL'} (${this.formatBytes(test.memoryDelta)})`
    ).join('\n')}
` : ''}

Recommendations:
${this.generateRecommendations()}
    `.trim();

    return report;
  }

  /**
   * Get memory information from browser
   */
  private getMemoryInfo() {
    // Use performance.memory if available (Chrome/Edge)
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const memory = (performance as any).memory;
      return {
        totalJSHeapSize: memory.totalJSHeapSize || 0,
        usedJSHeapSize: memory.usedJSHeapSize || 0,
        jsHeapSizeLimit: memory.jsHeapSizeLimit || 0
      };
    }

    // Fallback for browsers without performance.memory
    return {
      totalJSHeapSize: 0,
      usedJSHeapSize: 0,
      jsHeapSizeLimit: 0
    };
  }

  /**
   * Estimate current store count
   */
  private estimateStoreCount(): number {
    // This would be injected by the RootStore during initialization
    return (window as any).__STORE_COUNT__ || 0;
  }

  /**
   * Estimate current reaction count
   */
  private estimateReactionCount(): number {
    // This would be injected by the ReactionManager
    return (window as any).__REACTION_COUNT__ || 0;
  }

  /**
   * Estimate current event listener count
   */
  private estimateEventListenerCount(): number {
    // This is harder to measure accurately, but we can provide an estimate
    return (window as any).__EVENT_LISTENER_COUNT__ || 0;
  }

  /**
   * Force garbage collection if available
   */
  private forceGarbageCollection(): void {
    if (typeof window !== 'undefined' && (window as any).gc) {
      (window as any).gc();
    }
  }  /**
   * Force multiple rounds of garbage collection with delays
   */
  private async aggressiveGarbageCollection(): Promise<void> {
    // Reduced to 2 rounds to be less disruptive
    for (let i = 0; i < 2; i++) {
      this.forceGarbageCollection();
      // Shorter wait between collections
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Shorter final wait for any async cleanup
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  /**
   * Format bytes to human readable format
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate recommendations based on detected issues
   */
  private generateRecommendations(): string {
    const recommendations: string[] = [];

    if (this.detectedLeaks.some(l => l.leakType === 'reaction')) {
      recommendations.push("- Check reaction disposal in stores");
    }

    if (this.detectedLeaks.some(l => l.leakType === 'event-listener')) {
      recommendations.push("- Verify event listener cleanup");
    }

    if (this.testResults.some(t => !t.passed)) {
      recommendations.push("- Review failed disposal tests");
    }

    if (recommendations.length === 0) {
      recommendations.push("- Memory management appears healthy");
    }

    return recommendations.join('\n');
  }

  /**
   * Export all data for external analysis
   */
  exportData() {
    return {
      snapshots: this.snapshots,
      testResults: this.testResults,
      detectedLeaks: this.detectedLeaks,
      generatedAt: Date.now()
    };
  }

  /**
   * Clear all monitoring data
   */
  reset(): void {
    this.snapshots = [];
    this.testResults = [];
    this.detectedLeaks = [];
    this.baselineSnapshot = null;
  }
}